/* components/patterns/chat/Composer.jsx — поле ввода Ассистента (S5).
   Textarea (авторост, Enter=отправить / Shift+Enter=перенос), вложения (скрепка/DnD/
   Ctrl+V, лимиты 10 МБ/10 файлов), голос (MediaRecorder → /api/chats/transcribe/,
   таймер 180 с, предупреждение ≥150 с), полноэкранный drag-overlay (скрыт на тач),
   prefill+focus. Источник: 06 §2.6.
   Голос: реальная запись (getUserMedia/MediaRecorder) → распознавание на бэке →
   вставка текста в позицию курсора (или через пробел в конец, если курсора не было). */

import React from 'react';
import ReactDOM from 'react-dom';
import { WaveformRecorder } from './WaveformRecorder.jsx';
import { AttachmentChip } from '../../ui/AttachmentChip.jsx';
import { Button } from '../../ui/Button.jsx';
import { Textarea } from '../../ui/Field.jsx';
import { Icon } from '../../ui/Icon.jsx';
import { getOverlayRoot } from '../../../lib/overlay.jsx';
import { transcribeAudio, uploadAttachment, deleteAttachment } from '../../../lib/api/chats.js';

  const { useState, useRef, useEffect, useCallback } = React;

  const MAX_BYTES = 10 * 1024 * 1024;
  const MAX_FILES = 10;
  const MAX_RECORDING = 180;

  // выбираем поддерживаемый контейнер для MediaRecorder (Chrome → webm/opus, Safari → mp4)
  function pickRecorderMime() {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];
    for (const c of cands) { if (MediaRecorder.isTypeSupported(c)) return c; }
    return '';
  }

  function Composer({ value, onChange, onSubmit, busy, onStop, focusToken, isMobile }) {
    const [attachments, setAttachments] = useState([]);
    const [recState, setRecState] = useState('idle');     // idle | recording | transcribing
    const [recSeconds, setRecSeconds] = useState(0);
    const [recError, setRecError] = useState(null);
    const [isDragging, setDragging] = useState(false);
    const taRef = useRef(null);
    const fileRef = useRef(null);
    const recTimer = useRef(null);
    const dragDepth = useRef(0);
    const keyc = useRef(0);
    const mediaRef = useRef(null);     // MediaRecorder
    const chunksRef = useRef([]);      // куски аудио
    const streamRef = useRef(null);    // MediaStream — чтобы погасить дорожки микрофона
    const abortRef = useRef(null);     // отмена fetch-транскрипции при размонтировании
    const caretRef = useRef(null);     // позиция курсора, снятая ДО старта записи
    const recSecondsRef = useRef(0);   // длительность — источник правды для onstop
    const valueRef = useRef(value);    // актуальное value для вставки из async-колбэка
    const attsRef = useRef([]);        // зеркало attachments — для синхронного MAX_FILES-гейта и cleanup
    const uploadsRef = useRef(new Set());   // AbortController'ы летящих загрузок
    const prevUploadingRef = useRef(false); // предыдущее «идёт ли загрузка» — для авто-фокуса по завершении

    // держим valueRef в синхроне — вставка идёт из onstop-колбэка (стейл-замыкание)
    useEffect(() => { valueRef.current = value; }, [value]);
    useEffect(() => { attsRef.current = attachments; }, [attachments]);

    // prefill: фокус textarea при изменении focusToken
    useEffect(() => {
      if (focusToken && taRef.current) {
        taRef.current.focus();
        const v = taRef.current.value; taRef.current.setSelectionRange(v.length, v.length);
      }
    }, [focusToken]);

    // после завершения загрузки вложений (есть-загрузка → нет) возвращаем фокус в
    // поле ввода — чтобы сразу набрать сопроводительный текст к прикреплённым файлам.
    useEffect(() => {
      const uploadingNow = attachments.some((a) => a.uploading);
      if (prevUploadingRef.current && !uploadingNow && taRef.current) {
        const ta = taRef.current; ta.focus();
        const v = ta.value; try { ta.setSelectionRange(v.length, v.length); } catch { /* noop */ }
      }
      prevUploadingRef.current = uploadingNow;
    }, [attachments]);

    /* ── вложения ── */
    // Двухфазная схема: файл сразу льётся на бэк (POST /api/chats/attachments/),
    // ответ даёт ЧИСЛОВОЙ id, который при отправке уходит в attachment_ids и
    // привязывается к сообщению. Без реальной загрузки модель файлы не видит.
    const uploadOne = useCallback((file) => {
      if (attsRef.current.length >= MAX_FILES) return;
      const key = 'att' + (++keyc.current);
      const tooBig = file.size > MAX_BYTES;
      setAttachments((list) => (list.length >= MAX_FILES ? list : [...list, {
        key, id: null, name: file.name, size: file.size, mime: file.type || '',
        url: '#', uploading: !tooBig, error: tooBig ? 'Больше 10 МБ' : null,
      }]));
      if (tooBig) return;
      // Загрузку в полёте рвём при уходе с экрана: иначе она долетает уже
      // «в никуда» — чипа нет, id клиенту не вернулся, а на бэке родился файл,
      // который никто не удалит (cleanup ниже его не видит).
      const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      if (ctrl) uploadsRef.current.add(ctrl);
      uploadAttachment(file, ctrl ? { signal: ctrl.signal } : undefined).then((res) => {
        if (ctrl) uploadsRef.current.delete(ctrl);
        setAttachments((l) => l.map((a) => {
          if (a.key !== key) return a;
          if (res.ok && res.data && typeof res.data.id === 'number') {
            return { ...a, uploading: false, id: res.data.id, url: res.data.url || a.url, mime: res.data.mime || a.mime };
          }
          return { ...a, uploading: false, error: (res.data && res.data.detail) || 'Не удалось загрузить файл' };
        }));
      }).catch((e) => {
        if (ctrl) uploadsRef.current.delete(ctrl);
        if (e && e.name === 'AbortError') return;   // компонент размонтирован
        setAttachments((l) => l.map((a) => (a.key === key ? { ...a, uploading: false, error: 'Не удалось загрузить файл' } : a)));
      });
    }, []);

    const onPick = (e) => {
      const files = Array.from(e.target.files || []);
      files.forEach(uploadOne);
      e.target.value = '';
    };
    const removeAtt = (key) => {
      // удалить ещё не отправленный (не привязанный к сообщению) файл и на бэке —
      // не копим «висячие» вложения. Привязанные удалить нельзя (бэк вернёт 404), но
      // после отправки чипы уже очищены, так что сюда попадают только pending-файлы.
      const target = attsRef.current.find((a) => a.key === key);
      if (target && typeof target.id === 'number') deleteAttachment(target.id).catch(() => {});
      setAttachments((l) => l.filter((a) => a.key !== key));
    };

    /* ── drag-overlay (только hover-устройства) ── */
    useEffect(() => {
      // Гейт по ТАЧ-устройству, не по ширине окна: isMobile=true уже при <1680px (bp sm),
      // что молча отключало DnD файлов на любом MacBook. DnD из Finder/Explorer есть
      // у любого hover-устройства независимо от ширины.
      if (typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches) return;
      // 'public.file-url' — легаси-тип WebKit при перетаскивании файлов из Finder
      const hasFiles = (e) => { const t = Array.from((e.dataTransfer && e.dataTransfer.types) || []); return t.includes('Files') || t.includes('public.file-url'); };
      const onEnter = (e) => { if (hasFiles(e)) { e.preventDefault(); dragDepth.current++; setDragging(true); } };
      const onLeave = () => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); };
      // Safari/Mac: без dropEffect='copy' в dragover WebKit показывает «запрещено» и не шлёт drop
      const onOver = (e) => { e.preventDefault(); if (hasFiles(e)) { try { e.dataTransfer.dropEffect = 'copy'; } catch (_) {} } };
      const onDrop = (e) => {
        e.preventDefault(); dragDepth.current = 0; setDragging(false);
        const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
        files.forEach(uploadOne);
      };
      window.addEventListener('dragenter', onEnter);
      window.addEventListener('dragleave', onLeave);
      window.addEventListener('dragover', onOver);
      window.addEventListener('drop', onDrop);
      return () => {
        window.removeEventListener('dragenter', onEnter);
        window.removeEventListener('dragleave', onLeave);
        window.removeEventListener('dragover', onOver);
        window.removeEventListener('drop', onDrop);
      };
    }, [uploadOne]);

    // Ctrl+V изображения
    const onPaste = (e) => {
      const items = Array.from((e.clipboardData && e.clipboardData.items) || []);
      const imgs = items.filter((it) => it.kind === 'file' && it.type.startsWith('image/'));
      if (imgs.length) {
        imgs.forEach((it) => { const f = it.getAsFile(); if (f) uploadOne(f); });
      }
    };

    /* ── голос: запись (MediaRecorder) → транскрипция (/api/chats/transcribe/) → вставка ── */
    const stopStream = () => {
      const s = streamRef.current;
      if (s) { s.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };

    // Вставка распознанного текста: в позицию курсора (с пробелом перед, если
    // предыдущий символ не пробел), либо — если курсор не был установлен — через
    // пробел в конец строки.
    const insertTranscript = (raw) => {
      const text = (raw || '').trim();
      if (!text) return;
      const cur = valueRef.current || '';
      const caret = caretRef.current;
      let next, pos;
      if (caret && caret.start <= cur.length) {
        const before = cur.slice(0, caret.start);
        const after = cur.slice(caret.end);
        const chunk = (before && !/\s$/.test(before) ? ' ' : '') + text;
        next = before + chunk + after;
        pos = before.length + chunk.length;
      } else {
        next = cur + (cur && !/\s$/.test(cur) ? ' ' : '') + text;
        pos = next.length;
      }
      onChange(next);
      requestAnimationFrame(() => {
        const ta = taRef.current;
        if (ta) { ta.focus(); try { ta.setSelectionRange(pos, pos); } catch { /* noop */ } }
      });
    };

    // Запись остановлена → собираем blob, шлём на распознавание, вставляем результат.
    const finishRecording = async () => {
      stopStream();
      const chunks = chunksRef.current; chunksRef.current = [];
      const duration = Math.max(1, recSecondsRef.current || 0);
      const mr = mediaRef.current;
      const type = (mr && mr.mimeType) || (chunks[0] && chunks[0].type) || 'audio/webm';
      mediaRef.current = null;
      const resetIdle = () => { setRecState('idle'); setRecSeconds(0); recSecondsRef.current = 0; };
      if (!chunks.length) { resetIdle(); return; }

      const blob = new Blob(chunks, { type });
      abortRef.current = new AbortController();
      let res;
      try {
        res = await transcribeAudio(blob, duration, { signal: abortRef.current.signal });
      } catch (e) {
        abortRef.current = null;
        if (e && e.name === 'AbortError') return;   // компонент размонтирован
        setRecError('Не удалось распознать речь. Попробуйте ещё раз.');
        resetIdle(); return;
      }
      abortRef.current = null;
      if (!res.ok) {
        setRecError((res.data && res.data.detail) || 'Не удалось распознать речь. Попробуйте ещё раз.');
        resetIdle(); return;
      }
      insertTranscript(res.data && res.data.text);
      resetIdle();
    };

    // Снимаем позицию курсора на pointerdown кнопки микрофона — к моменту click
    // фокус уже уходит с textarea на кнопку, и selectionStart было бы потеряно.
    const captureCaret = () => {
      const ta = taRef.current;
      caretRef.current = (ta && document.activeElement === ta)
        ? { start: ta.selectionStart, end: ta.selectionEnd } : null;
    };

    const startRecording = async () => {
      if (recState !== 'idle') return;
      setRecError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setRecError('Запись недоступна: нужен защищённый (HTTPS) контекст.');
        return;
      }
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        setRecError(e && e.name === 'NotAllowedError'
          ? 'Нет доступа к микрофону. Разрешите запись в браузере.'
          : 'Микрофон недоступен.');
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];
      let mr;
      try {
        const mime = pickRecorderMime();
        mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch (e) {
        stopStream();
        setRecError('Запись не поддерживается в этом браузере.');
        return;
      }
      mediaRef.current = mr;
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
      mr.onstop = () => { finishRecording(); };
      try {
        mr.start();
      } catch (e) {
        stopStream(); mediaRef.current = null;
        setRecError('Не удалось начать запись.'); return;
      }
      recSecondsRef.current = 0; setRecSeconds(0); setRecState('recording');
      recTimer.current = setInterval(() => {
        const nextS = recSecondsRef.current + 1;
        recSecondsRef.current = nextS;
        setRecSeconds(nextS);
        if (nextS >= MAX_RECORDING) stopRecording();
      }, 1000);
    };

    // Завершить запись (кнопка/лимит): останавливаем рекордер → onstop → finishRecording.
    const stopRecording = () => {
      if (recTimer.current) { clearInterval(recTimer.current); recTimer.current = null; }
      const mr = mediaRef.current;
      if (mr && mr.state !== 'inactive') {
        setRecState('transcribing');
        try { mr.stop(); } catch (e) { finishRecording(); }
      } else {
        setRecState('idle'); setRecSeconds(0); recSecondsRef.current = 0;
        stopStream();
      }
    };

    // Закрытие вкладки/переход по внешней ссылке React-размонтированием НЕ
    // сопровождается — cleanup ниже не сработает, и прикреплённые файлы осели
    // бы на бэке висяками (главный их источник до 20.08.2026). `keepalive`
    // позволяет запросу пережить выгрузку страницы. `persisted` — уход в
    // bfcache: страница может вернуться с ЖИВЫМИ чипами, удалять нельзя.
    useEffect(() => {
      const onPageHide = (e) => {
        if (e && e.persisted) return;
        attsRef.current.forEach((a) => {
          if (typeof a.id === 'number') {
            deleteAttachment(a.id, { keepalive: true }).catch(() => {});
          }
        });
      };
      window.addEventListener('pagehide', onPageHide);
      return () => window.removeEventListener('pagehide', onPageHide);
    }, []);

    useEffect(() => () => {
      if (recTimer.current) clearInterval(recTimer.current);
      if (abortRef.current) abortRef.current.abort();
      uploadsRef.current.forEach((c) => { try { c.abort(); } catch { /* noop */ } });
      uploadsRef.current.clear();
      stopStream();
      // Ушли с экрана/сменили чат, не отправив прикреплённое — убираем файлы и
      // на бэке. Чипы всё равно живут только в стейте композера, так что после
      // ухода они уже недостижимы, а на сервере оставались «висяками» навсегда
      // (и до фикса 20.08.2026 накопленные висяки насмерть упирали гейт числа
      // вложений). Уже отправленные сюда не попадают: submit чистит attsRef
      // синхронно, а привязанные к сообщению бэк удалить и не даст (404).
      attsRef.current.forEach((a) => {
        if (typeof a.id === 'number') deleteAttachment(a.id).catch(() => {});
      });
      attsRef.current = [];
    }, []);

    /* ── отправка ── */
    // Пока хоть один файл грузится — не даём отправить (иначе строка ушла бы без
    // ещё не загруженного вложения, и модель его не увидела бы).
    const uploadingAny = attachments.some((a) => a.uploading);
    const canSend = !busy && !uploadingAny && (value.trim() || attachments.some((a) => !a.uploading && !a.error));
    const submit = () => {
      if (!canSend) return;
      const ids = attachments.filter((a) => !a.uploading && !a.error).map((a) => a.id);
      onSubmit(value.trim(), { attachment_ids: ids, attachments: attachments.filter((a) => !a.error) });
      // attsRef — СИНХРОННО: onSubmit может увести с экрана в этом же кадре, и
      // cleanup размонтирования увидел бы ещё не обнулённое зеркало стейта,
      // т.е. попытался бы удалить только что отправленные файлы.
      attsRef.current = [];
      setAttachments([]);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
    };

    const recording = recState === 'recording' || recState === 'transcribing';

    return (
      <div className="composer">
        <div className="composer__inner">
          {/* чипы вложений */}
          {attachments.length > 0 && (
            <div className="composer__atts">
              {attachments.map((a) => <AttachmentChip key={a.key} file={a} onRemove={() => removeAtt(a.key)} />)}
            </div>
          )}
          {/* ошибка микрофона */}
          {recError && <div className="composer__mic-err t-caption"><Icon name="alert-triangle" size={13} /> {recError}</div>}

          {recording ? (
            <WaveformRecorder recState={recState} recSeconds={recSeconds} onStop={() => stopRecording()} />
          ) : (
            <div className="composer__row">
              <button className="composer__icon-btn" onClick={() => fileRef.current && fileRef.current.click()}
                      aria-label="Прикрепить файл" disabled={busy}>
                <Icon name="paperclip" size={18} />
              </button>
              <input ref={fileRef} type="file" multiple hidden onChange={onPick} />
              <Textarea
                ref={taRef}
                className="composer__ta"
                minRows={1} maxRows={9}
                placeholder="Спросите ассистента…"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                aria-label="Сообщение"
              />
              <button className="composer__icon-btn" onPointerDown={captureCaret} onClick={startRecording}
                      aria-label="Записать голос" disabled={busy}>
                <Icon name="microphone" size={18} />
              </button>
              {busy && onStop ? (
                <button type="button" className="composer__stop" onClick={onStop}
                        aria-label="Остановить генерацию" title="Остановить генерацию">
                  <Icon name="stop" size={16} />
                </button>
              ) : (
                <Button variant="primary" iconOnly="send" className="composer__send"
                        onClick={submit} disabled={!canSend} aria-label="Отправить" />
              )}
            </div>
          )}
        </div>

        {/* полноэкранный drag-overlay */}
        {isDragging && getOverlayRoot && ReactDOM.createPortal(
          <div className="drag-overlay">
            <div className="drag-overlay__box">
              <Icon name="upload" size={32} />
              <div className="drag-overlay__title">Отпустите файлы здесь</div>
              <div className="drag-overlay__hint t-caption">До 10 файлов, по 10 МБ</div>
            </div>
          </div>,
          getOverlayRoot()
        )}
      </div>
    );
  }

export { Composer };
